import { NgModule } from '@angular/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

// Theme Components (shell + product-order-item tree + dialog-shared deps stay eager here;
// feature-only widgets were extracted to SharedUiModule).
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { VerticalMenuComponent } from './components/menu/vertical-menu/vertical-menu.component';
import { HorizontalMenuComponent } from './components/menu/horizontal-menu/horizontal-menu.component';
import { BackTopComponent } from './components/back-top/back-top.component';
import { ApplicationsComponent } from './components/applications/applications.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { CartSelectComponent } from './components/cart-select/select.component';

import { PipesModule as CorePipesModule } from '../pipes/pipes.module';
import { ProductPriceComponent } from './components/product-price/product-price.component';
import { OrderItemVariationsComponent } from './components/order-item-variations/order-item-variations.component';
import { ProductOrderItemComponent } from './components/product-order-item/product-order-item.component';
import { PriceTiersComponent } from '../modules/product/price-tiers/price-tiers.component';
import { ProductOrderItemHeaderComponent } from './components/product-order-item/product-order-item-header/product-order-item-header.component';
import { VendorModalComponent } from './dialogs/vendor-modal/vendor-modal.component';
import { ProductOrderItemPriceComponent } from './components/product-order-item/product-order-item-header/product-order-item-price/product-order-item-price.component';
import { ProductOrderItemInStockComponent } from './components/product-order-item/product-order-item-header/product-order-item-in-stock/product-order-item-in-stock.component';
import { ProductOrderItemMinMaxComponent } from './components/product-order-item/product-order-item-header/product-order-item-min-max/product-order-item-min-max.component';
import { ProductOrderItemDeliveryComponent } from './components/product-order-item/product-order-item-delivery/product-order-item-delivery-component';
import { ShipToAddressComponent } from '../modules/product/ship-to-address/ship-to-address.component';
import { ShippingLabelComponent } from '../modules/product/shipping-label/shipping-label.component';
import { DirectivesModule } from '../directives/directives.module';
import { HeaderCalloutComponent } from './components/header/header-callout/header-callout.component';
import { SapInfoComponent } from './components/sap-info/sap-info.component';
import { PaymentMethodSelectorComponent } from './components/payment-method-selector/payment-method-selector.component';
import { TrackingComponent } from '../modules/notifications/tracking/tracking.component';
import { FilterGroupComponent } from '../modules/products/filter/filter-group/filter-group.component';
import { FilterSelectedGroupComponent } from '../modules/products/filter/filter-selected-group/filter-selected-group.component';
import { ScrollContainerComponent } from './components/scroll-container/scroll-container.component';
import { SapSelectorComponent } from './components/sap-selector/sap-selector.component';
import { FavoriteToggleComponent } from './components/product-order-item/favorite-toggle/favorite-toggle.component';
import { ModalComponent } from '../components/modal.component';
import { SearchComponent } from '../components/search/search.component';
import { GlobalSearchContainerComponent } from '../components/global-search-container/global-search-container.component';
import { ProductGalleryComponent } from '../modules/product/product-gallery/product-gallery.component';
import { NgxGalleryModule } from '../ngx-gallery';
import { DeliveryTrackingComponent } from './components/product-order-item/product-order-item-delivery/delivery-tracking/delivery-tracking.component';
import { HeaderNavigationComponent } from './components/header/header-navigation/header-navigation.component';
import { MobileNavigationMenuComponent } from './components/header/mobile-navigation-menu/mobile-navigation-menu.component';
import { MobileCartMenuComponent } from './components/header/mobile-cart-menu/mobile-cart-menu.component';
import { ProductDetailsComponent } from '../modules/product/product-details/details.component';
import { InfiniteProgressComponent } from './components/infinite-progress/infinite-progress.component';
import { HeaderSearchComponent } from './components/header/header-search/header-search.component';
import { ProductRecommendedQuantityComponent } from './components/product-order-item/product-order-item-header/product-recommended-quantity/product-recommended-quantity.component';
import { ProductOrdersDetailsComponent } from './components/product-order-item/product-orders-details/product-orders-details.component';
import { ProductOrderDetailsTableComponent } from './components/product-order-item/product-orders-details/product-order-details-table/product-order-details-table.component';
import { CurrencyPriceComponent } from './components/currency-price/currency-price.component';
import { ClearableInputComponent } from './components/clearable-input/clearable-input.component';
import { ProductItemNumberComponent } from './components/product-item-number/product-item-number.component';
import { UserGuideComponent } from './components/header/user-guide/user-guide.component';
import {
    OnDemandMinMaxComponent
} from './components/product-order-item/product-order-item-header/on-demand-min-max/on-demand-min-max.component';
import {
    OnDemandSkuAvailableQtyComponent
} from './components/product-order-item/product-order-item-header/on-demand-sku-available-qty/on-demand-sku-available-qty.component';
import { RowFilterGroupComponent } from '../modules/products/row-filter/filter-group/row-filter-group.component';
import { InternalOrderCodeComponent } from './components/internal-order-code/internal-order-code.component';
import { EditContactDetailsComponent } from './components/addresses/edit-contact-details/edit-contact-details.component';
import { HeaderUpdateInfoComponent } from './components/header/header-update-info/header-update-info.component';
import { CustomizationControlComponent } from './components/product-order-item/product-order-item-header/customization-control/customization-control.component';
import {
    HeaderDistributionGroupComponent
} from './components/header/header-distribution-group/header-distribution-group.component';
import { AllocationGroupControlComponent } from './components/product-order-item/allocation-group-control/allocation-group-control.component';
import { OrderSettingsAllocationGroupControlComponent } from './dialogs/manage-order-list-addresses-modal/address-item/order-settings-allocation-group-control/order-settings-allocation-group-control.component';
import { OnDemandInventoryAllocationControlComponent } from './components/product-order-item/on-demand-allocation-group-control/on-demand-inventory-allocation-control.component';
import { InventoryAllocationStatusComponent } from './components/product-order-item/product-allocation-inventory/inventory-allocation-status.component';
import {
    SkuInventoryAllocationControlComponent
} from './components/product-order-item/sku-inventory-allocation-control/sku-inventory-allocation-control.component';

const COMPONENTS = [
    HeaderComponent,
    HeaderCalloutComponent,
    HeaderDistributionGroupComponent,
    HeaderUpdateInfoComponent,
    FooterComponent,
    SidebarComponent,
    VerticalMenuComponent,
    HorizontalMenuComponent,
    BackTopComponent,
    ApplicationsComponent,
    UserMenuComponent,
    CartSelectComponent,
    ProductPriceComponent,
    OrderItemVariationsComponent,
    ProductOrderItemComponent,
    PriceTiersComponent,
    ProductOrderItemHeaderComponent,
    VendorModalComponent,
    ProductOrderItemPriceComponent,
    ProductOrderItemMinMaxComponent,
    ProductOrderItemInStockComponent,
    ProductOrderItemDeliveryComponent,
    ShipToAddressComponent,
    ShippingLabelComponent,
    SapInfoComponent,
    PaymentMethodSelectorComponent,
    TrackingComponent,
    FilterGroupComponent,
    RowFilterGroupComponent,
    FilterSelectedGroupComponent,
    ScrollContainerComponent,
    SapSelectorComponent,
    FavoriteToggleComponent,
    ModalComponent,
    SearchComponent,
    GlobalSearchContainerComponent,
    ProductGalleryComponent,
    DeliveryTrackingComponent,
    HeaderNavigationComponent,
    MobileNavigationMenuComponent,
    MobileCartMenuComponent,
    ProductDetailsComponent,
    InfiniteProgressComponent,
    HeaderSearchComponent,
    ProductRecommendedQuantityComponent,
    ProductOrdersDetailsComponent,
    ProductOrderDetailsTableComponent,
    CurrencyPriceComponent,
    ClearableInputComponent,
    ProductItemNumberComponent,
    UserGuideComponent,
    OnDemandMinMaxComponent,
    OnDemandSkuAvailableQtyComponent,
    InternalOrderCodeComponent,
    EditContactDetailsComponent,
    CustomizationControlComponent,
    AllocationGroupControlComponent,
    OrderSettingsAllocationGroupControlComponent,
    OnDemandInventoryAllocationControlComponent,
    InventoryAllocationStatusComponent,
    SkuInventoryAllocationControlComponent,
];

@NgModule({
    imports: [
        CommonModule,
        NgbModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        CorePipesModule,
        DirectivesModule,
        NgxGalleryModule,
    ],
    declarations: [
        ...COMPONENTS,
    ],
    providers: [],
    exports: [
        ...COMPONENTS,
    ],
})
export class ThemeModule {
}
